'use client';

import * as React from 'react';
import cx from 'classnames';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';

import { AnalyticsRef } from '@components/analytics-ref';
import { Typography } from '@components/typography';

import { sectionLeadClassName } from './styles';

type AudienceKey = (typeof audiences)[number]['key'];

interface AudienceTabsProps {
  panels: Record<AudienceKey, React.ReactNode>;
}

/**
 * Page header with the audience toggle. Both panels stay mounted so each
 * audience's copy is in the server-rendered HTML.
 */
export const AudienceTabs: React.FC<AudienceTabsProps> = ({ panels }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  return (
    <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
      <section className="w-full bg-off-white">
        <div className="mx-auto flex w-full max-w-[964px] flex-col items-center px-4 py-12 text-center text-primary-main sm:px-8 sm:py-16">
          <Typography
            variant="title1"
            component="h1"
            className="!text-5xl !leading-[56px] sm:!text-h1"
          >
            How Savvy Works
          </Typography>
          {/* Both leads share one grid cell so the toggle doesn't move when the taller one swaps in. */}
          <div className="mt-4 grid">
            {audiences.map((audience, index) => {
              const isSelected = index === selectedIndex;
              return (
                <Typography
                  key={audience.key}
                  variant="body1"
                  component="p"
                  aria-hidden={!isSelected}
                  className={cx(
                    'font-medium [grid-area:1/1]',
                    sectionLeadClassName,
                    { invisible: !isSelected }
                  )}
                >
                  {audience.lead}
                </Typography>
              );
            })}
          </div>
          <TabList className="mt-8 flex w-full max-w-[650px] rounded-full border-[1.5px] border-primary-main p-1.5 sm:mt-10">
            {audiences.map((audience) => (
              <AnalyticsRef
                key={audience.key}
                name={audience.analyticsName}
                className="flex flex-1"
              >
                <Tab
                  aria-label={audience.label}
                  className="flex-1 whitespace-nowrap rounded-full border-[1.5px] border-transparent px-3 py-2 text-base font-medium text-primary-main transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main data-[hover]:bg-secondary-main/20 data-[selected]:border-primary-main data-[selected]:bg-secondary-main sm:px-6 sm:text-2xl"
                >
                  <span className="sm:hidden">{audience.shortLabel}</span>
                  <span className="hidden sm:inline">{audience.label}</span>
                </Tab>
              </AnalyticsRef>
            ))}
          </TabList>
        </div>
      </section>
      <TabPanels>
        {audiences.map((audience) => (
          <TabPanel key={audience.key} unmount={false}>
            {panels[audience.key]}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
};

const audiences = [
  {
    key: 'travelers',
    label: 'For Travelers',
    shortLabel: 'Travelers',
    analyticsName: 'how_it_works_tab_travelers',
    lead: 'Savvy connects travelers directly with professional property managers. Same properties you’d find on Airbnb. No markup. Average savings of $500 per booking.',
  },
  {
    key: 'propertyManagers',
    label: 'For Property Managers',
    shortLabel: 'Property Managers',
    analyticsName: 'how_it_works_tab_property_managers',
    lead: 'Savvy connects you directly with travelers through your PMS, so you keep the guest relationship, the data, and your revenue.',
  },
] as const;
